/**
 * Parsed error response from a MethodSCRIPT device
 */
export type ParsedError = {
    /**
     * The error code as a number (e.g., 0x4001 = 16385)
     * Null if error code could not be extracted
     */
    errorCode: number | null;

    /**
     * The human-readable description of the error
     * Null if error code not found in dictionary
     */
    description: string | null;

    /**
     * The line number where the error occurred (1-based)
     * Null if not present in error response
     */
    line: number | null;

    /**
     * The column number where the error occurred (1-based)
     * Null if not present in error response
     */
    column: number | null;

    /**
     * The original error message as received from the device
     */
    originalMessage: string;
};

// Import error codes from generated TypeScript file
import { errorCodesData } from './generated/error-codes.js';

// Create a map for faster lookups
const errorCodes = new Map<number, string>();
for (const [hexCode, description] of Object.entries(errorCodesData)) {
    const code = parseInt(hexCode, 16);
    errorCodes.set(code, description);
}

/**
 * Get the description for an error code
 */
export function getErrorDescription(errorCode: number): string | null {
    return errorCodes.get(errorCode) ?? null;
}

/**
 * Parse an error response string into a structured ParsedError object
 *
 * Supports the following error response formats:
 * - e!4001: Line 1, Col 12 (with leading 'e' and location)
 * - !0009: Line 1, Col 1 (without leading 'e', with location)
 * - !0023: Line 1 (line only, without column)
 * - Z!0006 (without location)
 *
 * @param errorResponse The error response string from the device
 * @returns A ParsedError object, or null if the response is not an error
 */
export function parseErrorResponse(errorResponse: string | null | undefined): ParsedError | null {
    if (!errorResponse || errorResponse.trim().length === 0) {
        return null;
    }

    // Check if the response contains an error code pattern (!XXXX)
    const errorCodeMatch = /!([0-9A-Fa-f]{4})/.exec(errorResponse);
    if (!errorCodeMatch) {
        return null;
    }

    // Extract error code
    const hexCode = errorCodeMatch[1];
    const errorCode = parseInt(hexCode, 16);

    // Try to extract line and column
    let line: number | null = null;
    let column: number | null = null;

    // First try to match line and column
    const lineColMatch = /Line\s+(\d+),\s*Col\s+(\d+)/i.exec(errorResponse);
    if (lineColMatch) {
        line = parseInt(lineColMatch[1], 10);
        column = parseInt(lineColMatch[2], 10);
    } else {
        // If no line+column match, try to match line only
        const lineOnlyMatch = /Line\s+(\d+)/i.exec(errorResponse);
        if (lineOnlyMatch) {
            line = parseInt(lineOnlyMatch[1], 10);
            // Column is null - will be handled in the UI layer
        }
    }

    // Look up description
    const description = getErrorDescription(errorCode);

    return {
        errorCode,
        description,
        line,
        column,
        originalMessage: errorResponse,
    };
}

/**
 * Check if a string appears to be an error response
 */
export function isErrorResponse(message: string | null | undefined): boolean {
    if (!message || message.trim().length === 0) {
        return false;
    }

    return /!([0-9A-Fa-f]{4})/.test(message);
}
